import React from 'react'
import { useI18n } from '../../i18n'
import { Button } from '../ui/Button'
import type { EvidenceReuseCandidate } from '../../services/complianceReuseApi'

type Props = {
  candidate: EvidenceReuseCandidate
  busy?: boolean
  onConfirm: (id: string) => void
  onReject: (id: string) => void
}

function scopeLabel(t: (k: string, f: string) => string, status: string) {
  switch (status) {
    case 'FULL_MATCH':
      return t('complianceReuse.scopeFullMatch', 'Full match')
    case 'PARTIAL_MATCH':
      return t('complianceReuse.partialMatch', 'Partial match')
    case 'LIMITED_MATCH':
      return t('complianceReuse.scopeLimitedMatch', 'Limited match')
    default:
      return t('complianceReuse.scopeCompatibility', 'Scope compatibility')
  }
}

export default function ReuseOpportunityCard({ candidate, busy, onConfirm, onReject }: Props) {
  const { t } = useI18n()
  const explanation =
    candidate.explanationPayload?.explanation?.summary ||
    candidate.reuseReason ||
    t('complianceReuse.reuseExplanation', 'Reuse explanation unavailable.')

  return (
    <article className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold text-slate-800 m-0">
            {t('complianceReuse.reuseSuggested', 'Reuse suggested')}: {candidate.sourceEvidence?.title || '—'}
          </p>
          <p className="text-xs text-slate-500 m-0 mt-0.5">
            {candidate.sourceStandardCode} → {candidate.targetStandardCode} ·{' '}
            {candidate.canonicalControl?.code || t('complianceReuse.canonicalControl', 'Canonical control')}
          </p>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
          {scopeLabel(t, candidate.scopeCompatibility)}
        </span>
      </div>
      <p className="text-xs text-slate-600 m-0">{explanation}</p>
      {candidate.informationSystem ? (
        <p className="text-[11px] text-slate-500 m-0">
          {t('complianceReuse.scopeCompatibility', 'Scope')}: {candidate.informationSystem.name}
        </p>
      ) : null}
      {candidate.status === 'SUGGESTED' ? (
        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" size="small" color="brand" disabled={busy} onClick={() => onConfirm(candidate.id)}>
            {t('complianceReuse.confirmReuse', 'Confirm reuse')}
          </Button>
          <Button type="button" size="small" color="outline" disabled={busy} onClick={() => onReject(candidate.id)}>
            {t('complianceReuse.rejectReuse', 'Reject reuse')}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-slate-500 m-0">{candidate.status}</p>
      )}
    </article>
  )
}
